<template>
  <div
    :class="classObject"
    class="form-checkbox-radio"
  >

    <div
      v-if="hasError"
      :id="errorId"
    >
      <a-form-error
        v-for="(error, index) in errors"
        v-if="error.show"
        :key="index"
      >
        {{ error.message }}
      </a-form-error>
    </div>

    <input
      ref="input"
      :aria-describedby="describedby"
      :aria-invalid="hasError ? 'true' : 'false'"
      :checked="checked"
      :id="id"
      :name="name"
      :type="type"
      :value="value"
      v-bind="$attrs"
      class="sr-only"
      @change="onChange"
    >

    <label
      :for="id"
      class="form-checkbox-radio__label"
    >
      <template
        v-if="label"
        v-html="label"
      >{{ label }}</template>
      <slot v-else />
    </label>

    <a-form-help-button
      v-if="help"
      :aria-controls="helpId"
      :aria-expanded="showHelp ? 'true' : 'false'"
      @click="showHelp = !showHelp"
    />

    <a-form-help
      v-if="help"
      v-show="showHelp"
      :aria-hidden="showHelp ? 'false' : 'true'"
      :id="helpId"
    >
      {{ help }}
    </a-form-help>

  </div>
</template>

<script>
import AFormError from '@/components/form/atoms/AFormError'
import AFormHelp from '@/components/form/atoms/AFormHelp'
import AFormHelpButton from '@/components/form/atoms/AFormHelpButton'

export default {
  name: 'BFormCheckboxRadio',

  components: {
    AFormError,
    AFormHelp,
    AFormHelpButton
  },

  inheritAttrs: false,

  model: {
    prop: 'modelValue',
    event: 'change'
  },

  props: {
    errors: {
      type: Array
    },
    hasError: {
      type: Boolean
    },
    help: {
      type: String
    },
    id: {
      type: String,
      required: true
    },
    isValid: {
      type: Boolean
    },
    label: {
      type: String
    },
    name: {
      type: String
    },
    value: {
      type: [String, Number]
    }
  },

  data () {
    return {
      showHelp: false
    }
  },

  computed: {
    classObject () {
      return {
        'has-error': this.hasError,
        'is-valid': this.isValid
      }
    },
    errorId () {
      return this.id + '-error'
    },
    helpId () {
      return this.id + '-help'
    },
    describedby () {
      let labels = []

      if (this.help) {
        labels.push(this.helpId)
      }

      if (this.hasError) {
        labels.push(this.errorId)
      }

      if (labels.length) {
        return labels.join(' ')
      }
    }
  },

  methods: {
    /**
     * Programatically set focus
     */
    focus () {
      this.$refs.input.focus()
    }
  }
}
</script>

<style scoped>
.form-checkbox-radio__label {
  display: block;
  position: relative;
  padding-left: 28px;
}

.form-checkbox-radio.has-error .form-checkbox-radio__label,
.form-checkbox-radio.is-valid .form-checkbox-radio__label {
  padding-right: 28px;
  background: center right / 20px no-repeat;
}

.form-checkbox-radio.has-error .form-checkbox-radio__label {
  background-image: url("~@/assets/form-error.svg");
}

.form-checkbox-radio.is-valid .form-checkbox-radio__label {
  background-image: url("~@/assets/form-success.svg");
}

.form-checkbox-radio__label::before,
.form-checkbox-radio__label::after {
  content: "";

  position: absolute;
  left: 0;
}

.form-checkbox-radio__label::before {
  top: 5px;
  width: 20px;
  height: 20px;

  border: 2px solid var(--color-brand);
  background: #fff;
}

:focus + .form-checkbox-radio__label::before {
  outline: 2px dotted var(--color-brand);
  outline-offset: 2px;
}

.form-checkbox-radio__label::after {
  opacity: 0;
  transition: opacity 100ms;
}

:checked + .form-checkbox-radio__label::after {
  opacity: 1;
}

[type="checkbox"] + .form-checkbox-radio__label::after {
  top: 7px;
  left: 6px;
  width: 7px;
  height: 12px;
  transform: rotate(45deg);

  border: 2px solid var(--color-brand);
  border-top: 0;
  border-left: 0;
}

[type="radio"] + .form-checkbox-radio__label::before,
[type="radio"] + .form-checkbox-radio__label::after {
  border-radius: 50%;
}

[type="radio"] + .form-checkbox-radio__label::after {
  top: 9px;
  left: 4px;
  width: 12px;
  height: 12px;

  background: var(--color-brand);
}
</style>
